// src/belediye/components/steps/closeApproval/CloseApprovalPanel.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";

import "./CloseApprovalPanel.css";

import {
  fetchCloseRequestForApplication,
  createCloseRequest,
  cancelCloseRequest,
  decideCloseRequest,
  setCloseRequestForApplication,
} from "../../../redux/slices/belediyeCloseApprovalSlice";

const safeStr = (v) => (v === null || v === undefined ? "" : String(v));
const isBlank = (s) => !safeStr(s).trim();

const formatDateTimeTR = (value) => {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return safeStr(value);
    return d.toLocaleString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return safeStr(value);
  }
};

const statusLabel = (s) => {
  if (s === "pending") return "Beklemede";
  if (s === "approved") return "Onaylandı";
  if (s === "rejected") return "Reddedildi";
  if (s === "cancelled") return "İptal Edildi";
  return safeStr(s) || "-";
};

const toMs = (dt) => {
  try {
    const d = new Date(dt);
    const t = d.getTime();
    return Number.isNaN(t) ? 0 : t;
  } catch {
    return 0;
  }
};

const isOverHours = (dtIso, hours) => {
  const t = toMs(dtIso);
  if (!t) return false;
  const diff = Date.now() - t;
  return diff >= hours * 60 * 60 * 1000;
};

const buildName = (display) => {
  const s = safeStr(display).trim();
  return s || "-";
};

const getRequestedAt = (cr) => cr?.requested_at || cr?.created_at || null;

const getProposedStatus = (cr) => safeStr(cr?.proposed_status || "").trim() || "—";
const getProposedNote = (cr) => safeStr(cr?.proposed_note || "").trim() || "—";
const getInternalNote = (cr) => safeStr(cr?.internal_note || "").trim() || "—";

// TR-safe “first letter upper” (basit)
const firstUpperTr = (text) => {
  const s = safeStr(text).trim();
  if (!s) return "";
  const lower = s.toLowerCase();
  if (lower === "çözüldü") return "Çözüldü";
  if (lower === "çözülemedi") return "Çözülemedi";
  try {
    return lower.charAt(0).toLocaleUpperCase("tr-TR") + lower.slice(1);
  } catch {
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
};

const isSolvedStatus = (status) => safeStr(status).trim().toLowerCase() === "çözüldü";
const isUnsolvedStatus = (status) => safeStr(status).trim().toLowerCase() === "çözülemedi";

const StatusChip = ({ value }) => {
  const raw = safeStr(value).trim();
  if (!raw || raw === "—") return <span className="cap-muted">—</span>;

  const label = firstUpperTr(raw);
  const solved = isSolvedStatus(raw);
  const unsolved = isUnsolvedStatus(raw);

  if (!solved && !unsolved) return <span className="cap-muted">{label}</span>;

  const cls = solved
    ? "cap-status-chip cap-status-chip--success"
    : "cap-status-chip cap-status-chip--danger";
  const Icon = solved ? CheckCircleIcon : XCircleIcon;

  return (
    <span className={cls}>
      <Icon className="cap-status-chip-icon" />
      {label}
    </span>
  );
};

export default function CloseApprovalPanel({
  mode, // "request" | "decide"
  applicationId,
  onAfterSubmit, // OPTIONAL
}) {
  const dispatch = useDispatch();
  const appIdNum = Number(applicationId) || 0;

  const closeApprovalState = useSelector((s) => s.belediyeCloseApproval);
  const entry = closeApprovalState?.byApplicationId?.[String(appIdNum)] || {};
  const current = entry?.current || null;

  const loading = Boolean(entry?.loading);
  const error = entry?.error || null;

  const creating = Boolean(closeApprovalState?.creating);
  const cancelling = Boolean(closeApprovalState?.cancelling);
  const deciding = Boolean(closeApprovalState?.deciding);

  //   Hard guard: aynı tick’te bile double submit engelle
  const inFlightRef = useRef(false);

  // ------------------------------------------------------------
  // Local state: request mode
  // ------------------------------------------------------------
  const [proposedStatus, setProposedStatus] = useState("çözüldü");
  const [proposedNote, setProposedNote] = useState(""); // zorunlu
  const [internalNote, setInternalNote] = useState(""); // isteğe bağlı

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState(""); // zorunlu

  // ------------------------------------------------------------
  // Local state: decide mode
  // ------------------------------------------------------------
  const [editMode, setEditMode] = useState(false);
  const [editStatus, setEditStatus] = useState(""); // boş => önerilen
  const [editNote, setEditNote] = useState(""); // boş => önerilen
  const [decisionNote, setDecisionNote] = useState(""); // reject zorunlu; approve isteğe bağlı (edit varsa)

  const [decideModalOpen, setDecideModalOpen] = useState(false);
  const [decideKind, setDecideKind] = useState(null); // approve|reject

  const [uiError, setUiError] = useState("");

  // ------------------------------------------------------------
  // Derived
  // ------------------------------------------------------------
  const hasAnyRequest = Boolean(current?.id);
  const isPending = current?.status === "pending";

  const requestedAt = useMemo(() => getRequestedAt(current), [current]);
  const requestAgeOver48h = useMemo(() => {
    const dt = getRequestedAt(current);
    return dt ? isOverHours(dt, 48) : false;
  }, [current]);

  const requestedBy = useMemo(
    () => buildName(current?.requested_by_display || current?.requested_by_name),
    [current]
  );

  const proposedSt = useMemo(() => getProposedStatus(current), [current]);
  const proposedNt = useMemo(() => getProposedNote(current), [current]);
  const internalNt = useMemo(() => getInternalNote(current), [current]);

  const chosenStatus = useMemo(
    () => safeStr(editStatus).trim() || proposedSt,
    [editStatus, proposedSt]
  );

  const hasEdits = useMemo(
    () => Boolean(safeStr(editStatus).trim() || safeStr(editNote).trim()),
    [editStatus, editNote]
  );

  // ------------------------------------------------------------
  // Fetch on mount: SADECE pending check
  // ------------------------------------------------------------
  useEffect(() => {
    if (!appIdNum) return;
    dispatch(fetchCloseRequestForApplication({ applicationId: appIdNum }));
  }, [dispatch, appIdNum]);

  //   edit açılınca textarea’yı önerilen not ile doldur
  useEffect(() => {
    if (!editMode) return;
    const suggested = proposedNt === "—" ? "" : proposedNt;
    setEditNote((prev) => (isBlank(prev) ? suggested : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  // ------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------
  const refetchPendingOnly = useCallback(async () => {
    setUiError("");
    try {
      await dispatch(fetchCloseRequestForApplication({ applicationId: appIdNum, force: true }))
        .unwrap?.();
    } catch {
      // ignore
    }
  }, [dispatch, appIdNum]);

  const handleCreate = async () => {
    if (inFlightRef.current) return;
    if (creating || cancelling || deciding) return;

    setUiError("");

    if (!appIdNum) return setUiError("applicationId bulunamadı.");
    if (isBlank(proposedStatus)) return setUiError("Kapanış durumu zorunlu.");
    if (isBlank(proposedNote)) return setUiError("Vatandaşa iletilecek not zorunludur.");

    inFlightRef.current = true;
    try {
      const res = await dispatch(
        createCloseRequest({
          application_id: appIdNum,
          proposed_status: proposedStatus,
          proposed_note: safeStr(proposedNote).trim(),
          internal_note: safeStr(internalNote).trim(),
        })
      ).unwrap?.();

      await refetchPendingOnly();
      onAfterSubmit?.(res);
    } catch {
      setUiError("Talep oluşturulamadı.");
    } finally {
      inFlightRef.current = false;
    }
  };

  const openCancelModal = () => {
    if (creating || cancelling || deciding) return;
    setUiError("");
    setCancelReason("");
    setCancelModalOpen(true);
  };

  const confirmCancel = async () => {
    if (inFlightRef.current) return;
    if (creating || cancelling || deciding) return;

    setUiError("");
    if (!current?.id) return;
    if (isBlank(cancelReason)) return setUiError("İptal gerekçesi zorunludur.");

    inFlightRef.current = true;
    try {
      const res = await dispatch(
        cancelCloseRequest({
          id: Number(current.id),
          applicationId: appIdNum,
          note: safeStr(cancelReason).trim(),
          internal_note: "",
        })
      ).unwrap?.();

      setCancelModalOpen(false);

      // UI: pending kartı kalksın
      dispatch(setCloseRequestForApplication({ applicationId: appIdNum, closeRequest: null }));

      await refetchPendingOnly();
      onAfterSubmit?.(res);
    } catch {
      setUiError("Talep iptal edilemedi.");
    } finally {
      inFlightRef.current = false;
    }
  };

  const openDecideModal = (kind) => {
    if (creating || cancelling || deciding) return;
    setUiError("");
    setDecideKind(kind);
    setDecideModalOpen(true);
  };

  const confirmDecide = async () => {
    //   double submit kesin kes
    if (inFlightRef.current) return;
    if (creating || cancelling || deciding) return;

    setUiError("");
    if (!current?.id || !decideKind) return;

    if (decideKind === "reject" && isBlank(decisionNote)) {
      return setUiError("Reddetmek için karar notu zorunludur.");
    }

    inFlightRef.current = true;
    try {
      const payload = {
        id: Number(current.id),
        applicationId: appIdNum,
        decision: decideKind,
        decision_note: safeStr(decisionNote).trim(),
        internal_note: "",
        ...(safeStr(editStatus).trim() ? { final_status: safeStr(editStatus).trim() } : {}),
        ...(safeStr(editNote).trim() ? { final_note: safeStr(editNote).trim() } : {}),
        refreshApplication: true,
      };

      const res = await dispatch(decideCloseRequest(payload)).unwrap?.();

      setDecideModalOpen(false);
      setDecideKind(null);

      setEditMode(false);
      setEditStatus("");
      setEditNote("");
      setDecisionNote("");

      await refetchPendingOnly();
      onAfterSubmit?.(res);
    } catch {
      setUiError("Karar işlemi başarısız.");
    } finally {
      inFlightRef.current = false;
    }
  };

  //   Tek buton: hem statü hem not için “öneriyi uygula”
  const applySuggestedAll = () => {
    if (creating || cancelling || deciding) return;
    setEditStatus("");
    setEditNote(proposedNt === "—" ? "" : proposedNt);
  };

  // ------------------------------------------------------------
  // UI blocks
  // ------------------------------------------------------------
  const renderHeader = () => (
    <div className="cap-header">
      <div className="cap-title-row">
        <ShieldCheckIcon className="cap-title-icon" />
        <h3 className="cap-title">Kapatma Talebi </h3>

        <button
          type="button"
          className="cap-icon-btn"
          onClick={refetchPendingOnly}
          title="Yenile"
          aria-label="Yenile"
          disabled={loading || creating || cancelling || deciding || inFlightRef.current}
        >
          <ArrowPathIcon />
        </button>
      </div>

      {(uiError || error) && (
        <div className="cap-alert cap-alert-error">
          <ExclamationTriangleIcon className="cap-alert-icon" />
          <div>{uiError || safeStr(error)}</div>
        </div>
      )}
    </div>
  );

  // “Mevcut Talep / Gelen Talep” pinned kart
  const renderPinnedRequestCard = ({ title, statusText, dateText, dateDanger, children }) => {
    return (
      <div className="cap-history-item cap-history-item--pinned">
        <div className="cap-history-head cap-history-head--pinned">
          <div className="cap-history-action">
            {title} <span className="cap-pinned-dot">•</span>{" "}
            <span className="cap-pinned-status">{statusText}</span>
          </div>

          <div
            className="cap-history-date"
            style={dateDanger ? { color: "#b91c1c", fontWeight: 750 } : undefined}
            title={dateDanger ? "48 saatten eski" : undefined}
          >
            <CalendarDaysIcon className="cap-mini-icon" />
            {dateText}
            {dateDanger ? <span style={{ marginLeft: 6 }}>(48+ saat)</span> : null}
          </div>
        </div>

        <div className="cap-acc-body cap-acc-body--pinned">{children}</div>
      </div>
    );
  };

  const renderRequestMode = () => {
    if (hasAnyRequest && isPending) {
      return (
        <>
          {renderPinnedRequestCard({
            title: "Mevcut Talep",
            statusText: statusLabel(current?.status),
            dateText: formatDateTimeTR(requestedAt),
            dateDanger: false,
            children: (
              <>
                <div className="cap-kv">
                  <div className="cap-k">Talep Eden</div>
                  <div className="cap-v">{buildName(current?.requested_by_display)}</div>

                  <div className="cap-k">İstenen Statü</div>
                  <div className="cap-v cap-note">
                    <StatusChip value={proposedSt} />
                  </div>

                  <div className="cap-k">Gerekçe</div>
                  <div className="cap-v cap-note">{internalNt}</div>

                  <div className="cap-k">Vatandaşa Gidecek Not</div>
                  <div className="cap-v cap-note">{proposedNt}</div>
                </div>

                <div className="cap-divider" />

                <div className="cap-actions">
                  <button
                    type="button"
                    className="cap-btn cap-btn-danger"
                    onClick={openCancelModal}
                    disabled={cancelling || creating || deciding || inFlightRef.current}
                  >
                    Talebi İptal Et
                  </button>
                </div>
              </>
            ),
          })}

          {cancelModalOpen && (
            <div
              className="cap-modal-backdrop"
              onMouseDown={() => {
                if (cancelling || inFlightRef.current) return;
                setCancelModalOpen(false);
              }}
            >
              <div className="cap-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="cap-modal-title-row">
                  <ExclamationTriangleIcon className="cap-modal-icon" />
                  <h3 className="cap-modal-title">Talep iptal edilecek</h3>
                </div>

                <div className="cap-modal-text">
                  İptal işlemi geri alınamaz. Lütfen iptal gerekçesini yazın.
                </div>

                <div className="cap-field">
                  <label className="cap-label">
                    İptal Gerekçesi <span className="cap-muted">(zorunlu)</span>
                  </label>
                  <textarea
                    className="cap-textarea"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Örn: Yanlış talep oluşturuldu..."
                    rows={3}
                    disabled={cancelling || inFlightRef.current}
                  />
                </div>

                <div className="cap-actions cap-actions-split">
                  <button
                    type="button"
                    className="cap-btn cap-btn-secondary"
                    onClick={() => setCancelModalOpen(false)}
                    disabled={cancelling || inFlightRef.current}
                  >
                    Vazgeç
                  </button>

                  <button
                    type="button"
                    className="cap-btn cap-btn-danger"
                    onClick={confirmCancel}
                    disabled={cancelling || inFlightRef.current}
                  >
                    İptal Et
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    return (
      <div className="ps-final-root" translate="no">
        <div className="ps-final-header">
          
          <div className="ps-final-info-box">
            <div className="ps-final-info-icon-main">
              <InformationCircleIcon />
            </div>
            <div className="ps-final-info-text">
              <div>Başvuruyu kapatmak için kapatma talebi oluşturun.</div>
              <div>
                <strong>Vatandaşa iletilecek not zorunludur.</strong> Gerekçe isteğe bağlı doldurulabilir.
              </div>
            </div>
          </div>
        </div>

        <div className="ps-final-status-options">
          <button
            type="button"
            className={`ps-final-option ps-final-option--positive ${
              proposedStatus === "çözüldü" ? "ps-final-option--active" : ""
            }`}
            onClick={() => setProposedStatus("çözüldü")}
            disabled={creating || cancelling || deciding || inFlightRef.current}
          >
            <div className="ps-final-option-main">
              <CheckCircleIcon className="ps-final-option-icon ps-final-option-icon--positive" />
              <div className="ps-final-option-text">
                <b>Çözüldü</b>
                <span>Başvuru başarıyla sonuçlandırılır ve kapatılır.</span>
              </div>
            </div>
            {proposedStatus === "çözüldü" && <span className="ps-final-option-badge">Seçildi</span>}
          </button>

          <button
            type="button"
            className={`ps-final-option ps-final-option--negative ${
              proposedStatus === "çözülemedi" ? "ps-final-option--active" : ""
            }`}
            onClick={() => setProposedStatus("çözülemedi")}
            disabled={creating || cancelling || deciding || inFlightRef.current}
          >
            <div className="ps-final-option-main">
              <XCircleIcon className="ps-final-option-icon ps-final-option-icon--negative" />
              <div className="ps-final-option-text">
                <b>Çözülemedi</b>
                <span>Teknik, hukuki veya idari sebeplerle başvuru kapatılır.</span>
              </div>
            </div>
            {proposedStatus === "çözülemedi" && <span className="ps-final-option-badge">Seçildi</span>}
          </button>
        </div>

        <div className="ps-final-note-box">
          <label className="ps-final-note-label">
            Vatandaşa iletilecek not <span className="ps-final-required">*</span>
          </label>
          <textarea
            className="ps-final-textarea"
            rows={4}
            value={proposedNote}
            onChange={(e) => setProposedNote(e.target.value)}
            placeholder="Vatandaşa iletilecek açıklamayı buraya yazın..."
            disabled={creating || cancelling || deciding || inFlightRef.current}
          />
        </div>

        <div className="ps-final-note-box" style={{ marginTop: 10 }}>
          <label className="ps-final-note-label">
            Gerekçe <span className="cap-muted">(isteğe bağlı)</span>
          </label>
          <textarea
            className="ps-final-textarea"
            rows={3}
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            placeholder="Birim içi gerekçe / açıklama..."
            disabled={creating || cancelling || deciding || inFlightRef.current}
          />
        </div>

        <div className="ps-final-actions">
          <button
            type="button"
            className="ps-final-button ps-final-button-primary"
            onClick={handleCreate}
            disabled={creating || cancelling || deciding || inFlightRef.current}
          >
            Talep Gönder
          </button>
        </div>
      </div>
    );
  };

  const renderDecideMode = () => {
    const hasPending = hasAnyRequest && isPending;

    if (!hasPending) {
      return (
        <div className="cap-alert cap-alert-info">
          <InformationCircleIcon className="cap-alert-icon" />
          <div>
            Bu başvuru için beklemede kapatma talebi yok.
            <br />
            Başvuruyu kapatmak istiyorsanız aşağıdaki “Sonuçlandırma” alanını kullanabilirsiniz.
          </div>
        </div>
      );
    }

    return (
      <>
        {renderPinnedRequestCard({
          title: "Gelen Talep",
          statusText: statusLabel(current?.status),
          dateText: formatDateTimeTR(requestedAt),
          dateDanger: requestAgeOver48h,
          children: (
            <>
              <div className="cap-kv">
                <div className="cap-k">Talep Eden</div>
                <div className="cap-v">{requestedBy}</div>

                <div className="cap-k">İstenen Statü</div>
                <div className="cap-v cap-note">
                  <StatusChip value={proposedSt} />
                </div>

                <div className="cap-k">Gerekçe</div>
                <div className="cap-v cap-note">{internalNt}</div>

                <div className="cap-k">Vatandaşa Gidecek Not</div>
                <div className="cap-v cap-note">{proposedNt}</div>
              </div>

              <div className="cap-divider" />

              {editMode && (
                <div className="cap-edit">
                  <div className="cap-edit-head">
                    <div className="cap-card-title" style={{ margin: 0 }}>
                      Düzenleme
                    </div>

                    <button
                      type="button"
                      className="cap-btn cap-btn-secondary cap-btn-inline"
                      onClick={applySuggestedAll}
                      disabled={deciding || creating || cancelling || inFlightRef.current}
                      title="Önerilen statü + önerilen notu uygula"
                    >
                      Öneriyi Uygula
                    </button>
                  </div>

                  <div className="cap-field">
                    <label className="cap-label">Statü</label>

                    <div className="cap-radio-row">
                      <button
                        type="button"
                        className={[
                          "cap-chip cap-chip--success",
                          firstUpperTr(chosenStatus) === "Çözüldü" ? "cap-chip-active" : "",
                        ].join(" ")}
                        onClick={() => setEditStatus("çözüldü")}
                        disabled={deciding || creating || cancelling || inFlightRef.current}
                      >
                        <CheckCircleIcon className="cap-mini-icon cap-mini-icon--success" />
                        Çözüldü
                      </button>

                      <button
                        type="button"
                        className={[
                          "cap-chip cap-chip--danger",
                          firstUpperTr(chosenStatus) === "Çözülemedi" ? "cap-chip-active" : "",
                        ].join(" ")}
                        onClick={() => setEditStatus("çözülemedi")}
                        disabled={deciding || creating || cancelling || inFlightRef.current}
                      >
                        <XCircleIcon className="cap-mini-icon cap-mini-icon--danger" />
                        Çözülemedi
                      </button>
                    </div>
                  </div>

                  <div className="cap-field">
                    <label className="cap-label">Vatandaşa Gidecek Not</label>
                    <textarea
                      className="cap-textarea"
                      value={editNote}
                      onChange={(e) => setEditNote(e.target.value)}
                      placeholder="Vatandaşa iletilecek notu düzenleyin…"
                      rows={3}
                      disabled={deciding || creating || cancelling || inFlightRef.current}
                    />
                  </div>

                  {hasEdits && (
                    <div className="cap-field">
                      <label className="cap-label">
                        Karar Notu <span className="cap-muted">(isteğe bağlı)</span>
                      </label>
                      <textarea
                        className="cap-textarea"
                        value={decisionNote}
                        onChange={(e) => setDecisionNote(e.target.value)}
                        placeholder="İsterseniz değişiklik gerekçesi yazabilirsiniz…"
                        rows={3}
                        disabled={deciding || creating || cancelling || inFlightRef.current}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="cap-actions cap-actions-3">
                <button
                  type="button"
                  className="cap-btn cap-btn-danger"
                  onClick={() => openDecideModal("reject")}
                  disabled={deciding || creating || cancelling || inFlightRef.current}
                >
                  Talebi Reddet
                </button>

                <div className="cap-actions-right">
                  <button
                    type="button"
                    className="cap-btn cap-btn-secondary"
                    onClick={() => {
                      if (creating || cancelling || deciding || inFlightRef.current) return;

                      if (editMode) {
                        setEditMode(false);
                        setEditStatus("");
                        setEditNote("");
                        setDecisionNote("");
                      } else {
                        setEditMode(true);
                      }
                    }}
                    disabled={deciding || creating || cancelling || inFlightRef.current}
                  >
                    {editMode ? "Düzenlemeyi Kapat" : "Düzenle / Değiştir"}
                  </button>

                  <button
                    type="button"
                    className="cap-btn cap-btn-primary"
                    onClick={() => openDecideModal("approve")}
                    disabled={deciding || creating || cancelling || inFlightRef.current}
                  >
                    Talebi Onayla
                  </button>
                </div>
              </div>
            </>
          ),
        })}

        {decideModalOpen && (
          <div
            className="cap-modal-backdrop"
            onMouseDown={() => {
              if (deciding || inFlightRef.current) return;
              setDecideModalOpen(false);
            }}
          >
            <div className="cap-modal" onMouseDown={(e) => e.stopPropagation()}>
              <div className="cap-modal-title-row">
                <InformationCircleIcon className="cap-modal-icon" />
                <h3 className="cap-modal-title">
                  {decideKind === "reject" ? "Talep reddedilecek" : "Talep onaylanacak"}
                </h3>
              </div>

              <div className="cap-modal-text">
                {decideKind === "reject"
                  ? "Reddetme işlemi sonrası personel tekrar talep oluşturabilir."
                  : "Onay sonrası başvuru kapatılacaktır."}
              </div>

              {decideKind === "reject" && (
                <div className="cap-field">
                  <label className="cap-label">
                    Karar Notu <span className="cap-muted">(zorunlu)</span>
                  </label>
                  <textarea
                    className="cap-textarea"
                    value={decisionNote}
                    onChange={(e) => setDecisionNote(e.target.value)}
                    placeholder="Reddetme gerekçesini yazın…"
                    rows={3}
                    disabled={deciding || inFlightRef.current}
                  />
                </div>
              )}

              {decideKind === "approve" && hasEdits && (
                <div className="cap-field">
                  <label className="cap-label">
                    Karar Notu <span className="cap-muted">(isteğe bağlı)</span>
                  </label>
                  <textarea
                    className="cap-textarea"
                    value={decisionNote}
                    onChange={(e) => setDecisionNote(e.target.value)}
                    placeholder="İsterseniz kısa bir not ekleyebilirsiniz…"
                    rows={3}
                    disabled={deciding || inFlightRef.current}
                  />
                </div>
              )}

              <div className="cap-actions cap-actions-split">
                <button
                  type="button"
                  className="cap-btn cap-btn-secondary"
                  onClick={() => setDecideModalOpen(false)}
                  disabled={deciding || inFlightRef.current}
                >
                  Vazgeç
                </button>

                <button
                  type="button"
                  className={decideKind === "reject" ? "cap-btn cap-btn-danger" : "cap-btn cap-btn-primary"}
                  onClick={confirmDecide}
                  disabled={deciding || inFlightRef.current}
                >
                  {decideKind === "reject" ? "Reddet" : "Onayla"}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="cap-root">
      {renderHeader()}

      {loading && (
        <div className="cap-alert cap-alert-info">
          <ClockIcon className="cap-alert-icon" />
          <div>Yükleniyor…</div>
        </div>
      )}

      {mode === "request" ? renderRequestMode() : renderDecideMode()}
    </div>
  );
}